import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button, Container, Stack, Alert, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import QuestionProgress from '../components/Interview/QuestionProgress';
import RecordingControls from '../components/Interview/RecordingControls';
import VideoPreview from '../components/Interview/VideoPreview';
import HrAvatar from '../components/Interview/HrAvatar';
import { useVideoRecording } from '../hooks/useVideoRecording';
import { useInterview } from '../contexts/InterviewContext';
import { VoiceActivityStreamDetector } from 'components/Interview/VoiceActivityStreamDetector';

const CHUNK_UPLOAD_TIMEOUT_MS = 60_000;
const CONNECTION_PROBE_TIMEOUT_MS = 10_000;

type UploadError = 'timeout' | 'offline';

const classifyUploadError = (err: unknown): UploadError => {
  const anyErr = err as { code?: string; name?: string; message?: string };
  if (anyErr?.code === 'ECONNABORTED' || anyErr?.name === 'CanceledError') {
    return 'timeout';
  }
  return 'offline';
};

const VideoInterviewRecordPage = observer(() => {
  const navigate = useNavigate();
  const [isFinished, setIsFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  const [isStopping, setIsStopping] = useState(false);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [isProbing, setIsProbing] = useState(false);
  const uploadErrorRef = useRef<UploadError | null>(null);

  // Используем хук для записи видео
  // Используем контекст интервью
  const { interview, loading, error, submitRecording, sendVideoChunk, probeConnection } =
    useInterview();

  const onChunkReady = useCallback(
    async (chunk: Blob) => {
      // Если предыдущий чанк уже упал — не шлём следующие, чтобы не «пробовать через зияющую дыру».
      if (uploadErrorRef.current) {
        throw new Error('upload aborted by previous error');
      }
      try {
        await sendVideoChunk(chunk, { timeout: CHUNK_UPLOAD_TIMEOUT_MS });
      } catch (err) {
        const kind = classifyUploadError(err);
        uploadErrorRef.current = kind;
        setUploadError(kind);
        throw err;
      }
    },
    [sendVideoChunk]
  );

  const {
    isRecording,
    recordingTime,
    hasVideoAccess,
    videoRef,
    recordedBlob,
    startRecording,
    stopRecording,
    resetRecording,
    devicesError,
    clearDevicesError
  } = useVideoRecording({
    chunkInterval: 20000,
    onChunkReady,
    videoQuality: 'high'
  });

  // ВАЖНО: хуки должны вызываться до любых ранних return
  const currentIndex = useMemo(() => interview?.index ?? 0, [interview?.index]);
  const currentQuestion = useMemo(
    () => interview?.questions?.[currentIndex] ?? { title: '', text: '' },
    [interview?.questions, currentIndex]
  );
  const interviewCount = useMemo(() => interview?.count ?? 0, [interview?.count]);

  const containerSx = useMemo(
    () => ({ height: '100%', overflow: 'auto', ml: -3, mr: -3, pl: 3, pr: 3 }),
    []
  );
  const centerStackSx = useMemo(() => ({ mt: { xs: 3, sm: 6, height: '100%' } }), []);
  const controlsBoxSx = useMemo(() => ({ mt: 'auto !important' }), []);

  const headerContent = useMemo(
    () => <QuestionProgress maxCoount={interviewCount} questionIndex={currentIndex} />,
    [currentIndex, interviewCount]
  );

  useEffect(() => {
    if (interview && !interview?.vacancyId && process.env.REACT_APP_IS_HR_DEMO === 'true') {
      navigate('/main');
    }
  }, [interview]);

  const handleStopRecording = useCallback(async () => {
    setIsStopping(true);
    await stopRecording();
    setIsStopping(false);
    setIsFinished(true);
  }, [stopRecording]);

  const handleRetryAfterUploadError = useCallback(async () => {
    setUploadError(null);
    uploadErrorRef.current = null;
    // Запись уже остановлена эффектом ниже — сбрасываем blob и рестартим через startRecording-кнопку.
    try {
      setIsProbing(true);
      await probeConnection(CONNECTION_PROBE_TIMEOUT_MS);
      resetRecording();
    } catch (err) {
      const kind = classifyUploadError(err);
      uploadErrorRef.current = kind;
      setUploadError(kind);
    } finally {
      setIsProbing(false);
    }
  }, [probeConnection, resetRecording]);

  // Авто-стоп записи при ошибке аплоада чанка — иначе дальше пишем в «пустоту»
  // и получаем дыру в видео, делая интервью непригодным.
  useEffect(() => {
    if (!uploadError || !isRecording || isStopping) return;
    let cancelled = false;
    (async () => {
      setIsStopping(true);
      try {
        await stopRecording();
      } finally {
        if (!cancelled) setIsStopping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadError, isRecording, isStopping, stopRecording]);

  const handleStartClick = useCallback(async () => {
    try {
      setRecordingError(null);
      setUploadError(null);
      uploadErrorRef.current = null;

      // Pre-flight: проверяем, что сервер отвечает, до того как начнём копить чанки.
      setIsProbing(true);
      try {
        await probeConnection(CONNECTION_PROBE_TIMEOUT_MS);
      } catch (err) {
        const kind = classifyUploadError(err);
        setUploadError(kind);
        return;
      } finally {
        setIsProbing(false);
      }

      const selectedMicrophone = localStorage.getItem('selectedMicrophone') || '';
      const selectedCamera = localStorage.getItem('selectedCamera') || '';

      await startRecording(selectedMicrophone, selectedCamera);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : devicesError || 'Произошла ошибка при запуске записи';
      setRecordingError(errorMessage);
    }
  }, [startRecording, devicesError, probeConnection]);

  const handleSubmitRecording = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setIsFinished(false);

    try {
      // Убеждаемся, что запись остановлена и все чанки отправлены
      if (isRecording) {
        await stopRecording();
      }

      // Ждем завершения отправки всех чанков перед вызовом submitRecording
      await submitRecording();

      if (!interview?.finish) {
        setIsFinished(false);
        resetRecording();
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Произошла ошибка при отправке видео'
      );
      console.error('Error submitting recording:', error);
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    interview?.finish,
    recordedBlob,
    resetRecording,
    submitRecording,
    isRecording,
    stopRecording
  ]);

  // Пока грузится следующий вопрос — не уходим на полноэкранный лоадер,
  // а оставляем того же аватара, который «думает» над вопросом.
  if (loading) {
    return (
      <Stack sx={containerSx}>
        {interview && headerContent}
        <Stack justifyContent="center" alignItems="center" spacing={3} sx={centerStackSx}>
          <HrAvatar thinking text="" />
        </Stack>
      </Stack>
    );
  }

  // Обработка ошибок
  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Произошла ошибка!
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Вернуться назад
        </Button>
      </Container>
    );
  }

  // Если нет данных интервью
  if (!interview) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Интервью не найдено
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Вернуться назад
        </Button>
      </Container>
    );
  }

  return (
    <Stack sx={containerSx}>
      {interview && headerContent}
      <Stack justifyContent="space-between" alignItems="center" spacing={3} sx={centerStackSx}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Вопрос {currentIndex + 1}
            {currentQuestion.title ? ` · ${currentQuestion.title}` : ''}
          </Typography>
          <HrAvatar
            text={currentQuestion.text}
          />
        </Box>
        {/* Блок «вы»: вебкамера + индикатор громкости микрофона рядом */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ position: 'relative', pb: { xs: 2, sm: 6 }, width: 260 }}
        >
          <VideoPreview
            ref={videoRef}
            isRecording={isRecording}
            hasVideoAccess={hasVideoAccess}
            corner
          />
          <VoiceActivityStreamDetector
            getStream={() => (videoRef.current?.srcObject as MediaStream | null) ?? null}
            isRecording={isRecording}
          />
        </Stack>
      </Stack>
      {(recordingError || devicesError) && (
        <Alert
          severity="error"
          sx={{ mt: 2, mb: 2 }}
          onClose={() => {
            setRecordingError(null);
            clearDevicesError();
          }}
        >
          {recordingError || devicesError}
        </Alert>
      )}
      {uploadError && (
        <Alert
          severity="error"
          sx={{ mt: 2, mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetryAfterUploadError}
              disabled={isProbing}
            >
              {isProbing ? 'Проверка…' : 'Повторить'}
            </Button>
          }
        >
          {uploadError === 'timeout'
            ? 'Сервер не отвечает — загрузка прервана по таймауту. Запись остановлена, проверьте соединение.'
            : 'Нет соединения с сервером. Запись остановлена, проверьте интернет и повторите попытку.'}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {submitError}
        </Alert>
      )}
      <Box sx={controlsBoxSx}>
        <RecordingControls
          isRecording={isRecording}
          isFinished={isFinished}
          onStopRecording={handleStopRecording}
          onStartRecording={handleStartClick}
          onSubmitRecording={handleSubmitRecording}
          isSubmitting={isSubmitting || isProbing}
          recordingTime={recordingTime}
          isStopping={isStopping}
        />
      </Box>
    </Stack>
  );
});

export default VideoInterviewRecordPage;
